import { useEffect, useState, useCallback } from 'react';
import { useRoomContext } from '@livekit/components-react';

export const COPILOT_TOPIC = 'meetgeek-copilot';

function normalizeBatch(message) {
  return {
    id: message.id || `sug-${message.ts || Date.now()}`,
    suggestions: message.suggestions || [],
    red_flags: message.red_flags || [],
    notes: message.notes || '',
    case: message.case || null,
    intervene: message.intervene !== false,
    ts: message.ts || Date.now(),
  };
}

function mergeSuggestionBatches(prev, incoming) {
  const byId = new Map();
  // Keep existing order, then fill gaps from history (newest first).
  [...incoming, ...prev].forEach((batch) => {
    if (!batch?.id || byId.has(batch.id)) return;
    byId.set(batch.id, batch);
  });
  return Array.from(byId.values())
    .sort((a, b) => (b.ts || 0) - (a.ts || 0))
    .slice(0, 30);
}

/**
 * Host-only listener for private co-pilot data messages.
 * Candidates (non-hosts) ignore the topic entirely.
 * Requests recent suggestion history when attaching to an existing Sage.
 */
function normalizeTranscriptLine(message) {
  return {
    id: message.utterance_id || `cap-${message.ts || Date.now()}`,
    speaker: message.speaker || '',
    text: String(message.text || '').trim(),
    partial: Boolean(message.partial),
    ts: message.ts || Date.now(),
  };
}

export function useCopilotMessages({ enabled = false } = {}) {
  const room = useRoomContext();
  const [suggestions, setSuggestions] = useState([]);
  const [ready, setReady] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [latestNotes, setLatestNotes] = useState('');
  const [redFlags, setRedFlags] = useState([]);
  const [transcriptLines, setTranscriptLines] = useState([]);
  const [hasPriorContext, setHasPriorContext] = useState(false);
  const [contextCandidateName, setContextCandidateName] = useState('');

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setLatestNotes('');
    setRedFlags([]);
  }, []);

  const resetSession = useCallback(() => {
    setSuggestions([]);
    setReady(false);
    setSessionComplete(false);
    setLatestNotes('');
    setRedFlags([]);
    setTranscriptLines([]);
    setHasPriorContext(false);
    setContextCandidateName('');
  }, []);

  const requestHistory = useCallback(async () => {
    if (!room?.localParticipant) return;
    try {
      const payload = new TextEncoder().encode(
        JSON.stringify({ type: 'copilot_sync_request', ts: Date.now() })
      );
      await room.localParticipant.publishData(payload, {
        reliable: true,
        topic: COPILOT_TOPIC,
      });
    } catch (e) {
      console.warn('Failed to request Sage suggestion history:', e);
    }
  }, [room]);

  useEffect(() => {
    if (!enabled || !room) {
      return undefined;
    }

    const handleData = (payload, participant, _kind, topic) => {
      if (topic && topic !== COPILOT_TOPIC) {
        return;
      }
      let message;
      try {
        const text =
          typeof payload === 'string'
            ? payload
            : new TextDecoder().decode(payload);
        message = JSON.parse(text);
      } catch {
        return;
      }
      if (!message || typeof message !== 'object') {
        return;
      }

      switch (message.type) {
        case 'copilot_ready':
          setReady(true);
          setSessionComplete(false);
          setHasPriorContext(Boolean(message.has_prior_context));
          if (message.candidate_name) {
            setContextCandidateName(message.candidate_name);
          }
          // Sage (re)announced — pull recent history for this host.
          requestHistory();
          break;
        case 'copilot_suggestion':
          // Intervene-only: ignore silence payloads if any slip through.
          if (message.intervene === false) break;
          setSuggestions((prev) => mergeSuggestionBatches(prev, [normalizeBatch(message)]));
          setLatestNotes(message.notes || '');
          setRedFlags(message.red_flags || []);
          setReady(true);
          break;
        case 'copilot_suggestion_history': {
          const batches = (message.batches || []).map(normalizeBatch);
          setSuggestions((prev) => mergeSuggestionBatches(prev, batches));
          if (batches[0]) {
            setLatestNotes(batches[0].notes || '');
            setRedFlags(batches[0].red_flags || []);
          }
          setReady(true);
          break;
        }
        case 'copilot_session_complete':
          setSessionComplete(true);
          break;
        case 'copilot_transcript': {
          const line = normalizeTranscriptLine(message);
          if (!line.text) break;
          // Captions v1: keep finals; replace in-progress partial by utterance id.
          setTranscriptLines((prev) => {
            if (line.partial) {
              const without = prev.filter((p) => p.id !== line.id || !p.partial);
              return [...without, line].slice(-40);
            }
            const withoutPartial = prev.filter((p) => p.id !== line.id);
            return [...withoutPartial, { ...line, partial: false }].slice(-40);
          });
          break;
        }
        default:
          break;
      }
    };

    room.on('dataReceived', handleData);

    // Ask Sage for recent suggestions (no-op if Sage not present yet).
    requestHistory();

    return () => {
      room.off('dataReceived', handleData);
    };
  }, [enabled, room, requestHistory]);

  return {
    suggestions,
    ready,
    sessionComplete,
    latestNotes,
    redFlags,
    transcriptLines,
    hasPriorContext,
    contextCandidateName,
    clearSuggestions,
    resetSession,
  };
}
